/**
 * Chat Share Button
 *
 * Header control for personal-chat sharing actions.
 *
 * Primary responsibilities:
 * - Create and update public chat links
 * - Convert a personal chat into a group chat
 * - Reflect public chat state with a globe-only header affordance
 *
 * Dependencies:
 * - Chat and app contexts
 * - Chat update and group chat client services
 * - Dialog, button, and copy UI components
 */

'use client'

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { updateChatAtom } from '@/atoms/workspaceAtoms'
import { updateChat } from '@/db/chats'
import { getMessagesByChatId } from '@/db/messages'
import {
  IconBrandFacebook,
  IconBrandLinkedin,
  IconBrandReddit,
  IconBrandX,
  IconLink,
  IconLock,
  IconLockOpen,
  IconMessages,
  IconShare2,
  IconUsers,
  IconWorld,
  IconX
} from '@tabler/icons-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { getActivationChatType, trackChatShareClicked } from '@/lib/analytics/activation'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { useAuth } from '@/context/auth'
import {
  canBypassNexusSubscription,
  hasActiveNexusSubscription
} from '@/lib/subscription'
import { supabase } from '@/lib/supabase/browser-client'
import { createGroupChatFromSource } from '@/lib/services/chat-group-service'
import type { GroupChatResponseMode } from '@/types/chat-groups'
import type { Tables } from '@/supabase/types'
import { getWorkspaceRoleForUser } from '@/lib/workspaces/workspace-access'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

function resolveShareUrl(messageId: string | null | undefined) {
  if (!messageId) {
    return ''
  }

  const path = `/chat/share/${messageId}`

  if (typeof window === 'undefined') {
    return path
  }

  return `${window.location.origin}${path}`
}

export function ShareChatButton() {
  const { navigateToHref } = useSplitPaneNavigation()
  const updateChatInAtom = useSetAtom(updateChatAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [isTeamFeedLoading, setIsTeamFeedLoading] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [shareUrl, setShareUrl] = useState('')
  const [createResponseMode, setCreateResponseMode] = useState<GroupChatResponseMode>('always')
  const [workspaceRole, setWorkspaceRole] = useState<string | null>(null)
  const [isWorkspaceRoleLoading, setIsWorkspaceRoleLoading] = useState(false)
  const { profile, setChats } = useContext(ChatbotUIContext)
  const { selectedChat, setSelectedChat } = useContext(ChatbotUIChatContext)
  const { user } = useAuth()

  const currentUserId = profile?.user_id ?? null
  const isPublicChat = Boolean(
    selectedChat?.sharing === 'public' && (selectedChat.last_shared_message_id || shareUrl)
  )

  // Team feed is a Nexus surface — hide (don't disable) unless entitled.
  // Matches ShareSpaceConversationToTeamFeedButton + POST conversation-snapshots.
  const nexusEntitled =
    hasActiveNexusSubscription(profile) ||
    canBypassNexusSubscription(profile, user?.email)

  const canConvertGroup = useMemo(() => {
    if (!selectedChat || !currentUserId || selectedChat.is_group_chat) {
      return false
    }

    if (selectedChat.user_id === currentUserId) {
      return true
    }

    return workspaceRole === 'owner' || workspaceRole === 'admin'
  }, [currentUserId, selectedChat, workspaceRole])

  const syncChatState = useCallback(
    (nextChat: Tables<'chats'>) => {
      setSelectedChat((previousChat) =>
        previousChat?.id === nextChat.id ? nextChat : previousChat
      )

      setChats((previousChats) => {
        const hasExistingChat = previousChats.some((chat) => chat.id === nextChat.id)

        if (!hasExistingChat) {
          return [nextChat, ...previousChats]
        }

        return previousChats.map((chat) => (chat.id === nextChat.id ? nextChat : chat))
      })

      updateChatInAtom(nextChat)
    },
    [setChats, setSelectedChat, updateChatInAtom]
  )

  const checkIfShared = useCallback(async () => {
    if (!selectedChat) return

    const { data } = await supabase
      .from('chats')
      .select('last_shared_message_id')
      .eq('id', selectedChat.id)
      .eq('sharing', 'public')
      .maybeSingle()

    if (data?.last_shared_message_id) {
      setShareUrl(resolveShareUrl(data.last_shared_message_id))
    } else {
      setShareUrl('')
    }
  }, [selectedChat])

  useEffect(() => {
    if (isDialogOpen) {
      void checkIfShared()
    }
  }, [checkIfShared, isDialogOpen])

  useEffect(() => {
    if (!selectedChat) {
      setShareUrl('')
      return
    }

    if (selectedChat.sharing === 'public' && selectedChat.last_shared_message_id) {
      setShareUrl(resolveShareUrl(selectedChat.last_shared_message_id))
      return
    }

    if (!isDialogOpen) {
      setShareUrl('')
    }
  }, [isDialogOpen, selectedChat?.id, selectedChat?.last_shared_message_id, selectedChat?.sharing])

  useEffect(() => {
    if (!selectedChat?.workspace_id || !currentUserId) {
      setWorkspaceRole(null)
      setIsWorkspaceRoleLoading(false)
      return
    }

    if (selectedChat.user_id === currentUserId) {
      setWorkspaceRole('owner')
      setIsWorkspaceRoleLoading(false)
      return
    }

    let isCancelled = false
    setIsWorkspaceRoleLoading(true)

    getWorkspaceRoleForUser(supabase, selectedChat.workspace_id, currentUserId)
      .then((role) => {
        if (!isCancelled) {
          setWorkspaceRole(role)
          setIsWorkspaceRoleLoading(false)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setWorkspaceRole(null)
          setIsWorkspaceRoleLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [currentUserId, selectedChat?.id, selectedChat?.user_id, selectedChat?.workspace_id])

  const handleShareChat = async () => {
    if (!selectedChat || !currentUserId) return

    try {
      setIsLoading(true)

      if (selectedChat.is_group_chat) {
        throw new Error('Group chats cannot be shared publicly')
      }

      const messages = await getMessagesByChatId(selectedChat.id)

      if (messages.length === 0) {
        toast.error('Send a message before creating a public link')
        return
      }

      const lastMessage = messages[messages.length - 1]
      const sharedAssistantMessage = [...messages]
        .reverse()
        .find((message) => message.role === 'assistant' && message.id)

      if (!lastMessage.id) {
        throw new Error('Last message does not have an id')
      }

      const updatedChat = await updateChat(selectedChat.id, {
        sharing: 'public',
        last_shared_message_id: lastMessage.id,
        shared_by: currentUserId,
        shared_at: new Date().toISOString()
      })

      syncChatState(updatedChat)
      setShareUrl(resolveShareUrl(lastMessage.id))
      if (sharedAssistantMessage?.id) {
        void trackMeaningfulOutcomeCompleted({
          surface: 'chat',
          outcome_type: 'saved_output',
          qualifying_action: 'shared',
          chat_id: selectedChat.id,
          message_id: sharedAssistantMessage.id,
          workspace_id: selectedChat.workspace_id ?? undefined,
          source: 'chat_share'
        })
      }
      toast.success(shareUrl ? 'Public link updated' : 'Public link created')
    } catch (error) {
      toast.error(
        error instanceof Error && error.message.trim()
          ? error.message
          : "Couldn't share this chat. Try again."
      )
      console.error('Error sharing chat:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleMakePrivate = async () => {
    if (!selectedChat?.id) return

    try {
      setIsLoading(true)
      const updatedChat = await updateChat(selectedChat.id, {
        sharing: 'private',
        last_shared_message_id: null,
        shared_by: null,
        shared_at: null
      })
      syncChatState(updatedChat)
      setShareUrl('')
      setIsDialogOpen(false)
      toast.success('Chat is now private')
    } catch (error) {
      toast.error("Couldn't make this chat private. Try again.")
      console.error('Error making chat private:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleConvertToGroup = async () => {
    if (!selectedChat?.id || !currentUserId || !canConvertGroup) {
      return
    }

    try {
      setIsLoading(true)
      const response = await createGroupChatFromSource(selectedChat.id, createResponseMode)

      syncChatState(response.chat)
      setIsDialogOpen(false)
      toast.success('Chat converted to group')
      navigateToHref(`/chat/${response.chat.id}?groupSettings=1`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to convert chat to group'
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleShareToTeamFeed = async () => {
    if (!selectedChat?.workspace_id || !currentUserId) {
      toast.error('Select a workspace chat first')
      return
    }

    if (!nexusEntitled) {
      toast.error('A Nexus subscription is required to share to team feed')
      return
    }

    try {
      setIsTeamFeedLoading(true)

      const messages = await getMessagesByChatId(selectedChat.id)
      if (messages.length === 0) {
        toast.error('Send a message before sharing to the team feed')
        return
      }

      const response = await authenticatedFetch(
        `/api/workspaces/${selectedChat.workspace_id}/conversation-snapshots`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId: selectedChat.id,
            title: selectedChat.name || 'Untitled chat',
            spaceId: selectedChat.space_id ?? null
          })
        }
      )

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null)
        const message =
          (errorBody && typeof errorBody.error === 'string' && errorBody.error) ||
          'Failed to share to team feed'
        throw new Error(message)
      }

      const created = (await response.json()) as { id?: string }
      toast.success('Shared to team feed')
      setIsDialogOpen(false)

      if (created.id) {
        navigateToHref(`/spaces/shared-chats/${created.id}`)
      } else {
        navigateToHref('/spaces/shared-chats')
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to share to team feed'
      toast.error(message)
    } finally {
      setIsTeamFeedLoading(false)
    }
  }

  const handleSocialShare = (platform: string) => {
    let url = ''
    const text = 'Check out this chat!'

    switch (platform) {
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
        break
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
        break
      case 'reddit':
        url = `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(text)}`
        break
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`
        break
    }

    window.open(url, '_blank')
  }

  const handleOpenDialog = async () => {
    void trackChatShareClicked({
      surface: 'authenticated_chat',
      chat_type: getActivationChatType({
        isGroupChat: selectedChat?.is_group_chat
      })
    })
    setIsDialogOpen(true)
    await checkIfShared()
  }

  if (selectedChat?.is_group_chat) return null
  if (!selectedChat) return null

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          aria-label={isPublicChat ? 'Manage public chat link' : 'Share chat'}
          onClick={handleOpenDialog}
          size="icon"
          title={isPublicChat ? 'Public chat link' : 'Share chat'}
          variant="ghost"
        >
          {isPublicChat ? <IconWorld stroke={1.5} /> : <IconShare2 stroke={1.5} />}
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-0 rounded-2xl p-0 sm:max-w-xl">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <IconShare2 className="size-4 text-muted-foreground" stroke={1.8} />
            Share chat
          </DialogTitle>
          <DialogClose asChild>
            <Button
              aria-label="Close share dialog"
              className="size-8 rounded-full"
              size="icon"
              variant="ghost"
            >
              <IconX className="size-4" />
            </Button>
          </DialogClose>
        </div>

        <div className="p-5">
          <section className="space-y-3 pb-5">
            <div className="flex items-start gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/30">
                <IconWorld className="size-4" stroke={1.7} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">Make public</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Anyone with the link or in the workspace can view this chat snapshot.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                className="h-10 flex-1"
                placeholder="Public link appears here"
                readOnly
                value={shareUrl}
              />
              <div className="flex gap-2">
                <Button
                  className="shrink-0 rounded-xl"
                  loading={isLoading}
                  onClick={handleShareChat}
                  variant="outline"
                >
                  <IconLink className="mr-2 size-4" />
                  {shareUrl ? 'Update' : 'Create'}
                </Button>
                {shareUrl ? (
                  <CopyButton
                    className="size-10 shrink-0 rounded-xl text-foreground"
                    value={shareUrl}
                    variant="outline"
                  />
                ) : null}
              </div>
            </div>

            {shareUrl ? (
              <div className="flex items-center gap-2 pt-1">
                <Button
                  aria-label="Share on LinkedIn"
                  className="size-8 rounded-full text-muted-foreground hover:text-foreground"
                  size="icon"
                  variant="ghost"
                  onClick={() => handleSocialShare('linkedin')}
                >
                  <IconBrandLinkedin className="size-4" />
                </Button>
                <Button
                  aria-label="Share on Facebook"
                  className="size-8 rounded-full text-muted-foreground hover:text-foreground"
                  size="icon"
                  variant="ghost"
                  onClick={() => handleSocialShare('facebook')}
                >
                  <IconBrandFacebook className="size-4" />
                </Button>
                <Button
                  aria-label="Share on Reddit"
                  className="size-8 rounded-full text-muted-foreground hover:text-foreground"
                  size="icon"
                  variant="ghost"
                  onClick={() => handleSocialShare('reddit')}
                >
                  <IconBrandReddit className="size-4" />
                </Button>
                <Button
                  aria-label="Share on X"
                  className="size-8 rounded-full text-muted-foreground hover:text-foreground"
                  size="icon"
                  variant="ghost"
                  onClick={() => handleSocialShare('twitter')}
                >
                  <IconBrandX className="size-4" />
                </Button>
              </div>
            ) : null}
          </section>

          {isPublicChat && (
            <section className="space-y-3 border-t border-border/60 pt-5 pb-2">
              <div className="flex items-start gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-red-50 dark:bg-red-950/30">
                  <IconLock className="size-4 text-red-600 dark:text-red-400" stroke={1.7} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">Make private</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Remove public access. The share link will stop working.
                  </p>
                </div>
              </div>
              <Button
                className="w-full rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/30"
                disabled={isLoading}
                loading={isLoading}
                onClick={handleMakePrivate}
                variant="outline"
              >
                <IconLockOpen className="mr-2 size-4" />
                Remove public link
              </Button>
            </section>
          )}

          {nexusEntitled ? (
            <section className="space-y-3 border-t border-border/60 pt-5">
              <div className="flex items-start gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/30">
                  <IconMessages className="size-4" stroke={1.7} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">Share to team feed</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Post a frozen snapshot to Shared chats. It will not update if
                    this conversation continues.
                  </p>
                </div>
              </div>
              <Button
                className="w-full rounded-xl"
                disabled={isTeamFeedLoading || isLoading}
                loading={isTeamFeedLoading}
                onClick={() => void handleShareToTeamFeed()}
                variant="outline"
              >
                <IconMessages className="mr-2 size-4" />
                Share to team feed
              </Button>
            </section>
          ) : null}

          <section className="space-y-3 border-t border-border/60 pt-5">
            <div className="flex items-start gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/30">
                <IconUsers className="size-4" stroke={1.7} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">Convert to group chat</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Keep this conversation and invite teammates into it.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                className={cn(
                  'h-9 rounded-xl border shadow-none',
                  createResponseMode === 'always'
                    ? 'border-foreground/15 bg-muted text-foreground'
                    : 'border-input bg-background text-muted-foreground hover:text-foreground'
                )}
                disabled={isLoading}
                onClick={() => setCreateResponseMode('always')}
                type="button"
                variant="outline"
              >
                Always reply
              </Button>
              <Button
                className={cn(
                  'h-9 rounded-xl border shadow-none',
                  createResponseMode === 'mention_only'
                    ? 'border-foreground/15 bg-muted text-foreground'
                    : 'border-input bg-background text-muted-foreground hover:text-foreground'
                )}
                disabled={isLoading}
                onClick={() => setCreateResponseMode('mention_only')}
                type="button"
                variant="outline"
              >
                Mention only
              </Button>
            </div>

            <Button
              className="w-full rounded-xl"
              disabled={!canConvertGroup || isWorkspaceRoleLoading}
              loading={isLoading}
              onClick={handleConvertToGroup}
              variant="secondary"
            >
              <IconUsers className="mr-2 size-4" />
              Convert to group chat
            </Button>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
